import { Helmet } from "react-helmet-async";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { ArrowRight, CheckCircle, Shield, Users, Headphones, TrendingUp, BarChart3, Star, AlertTriangle, MapPin, Users2, BarChart, MessageSquare, FileText, Phone, Trophy, Sparkles, ArrowUpRight, Coins, ArrowLeftRight, Settings, Zap } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { brandLogos } from "@/constants/brandLogos";
import { LANDING_COLORS } from "@/lib/landing-styles";
import { sendSiteLead, SITE_LEAD_ERROR } from "@/lib/siteLead";
import { RevealOnScroll, FloatingOrbs } from "@/components/landing";

// Page copy as data, so every card in a row shares one markup and one style.
const HERO_OUTCOMES = [
  { icon: Trophy, label: "Rank in the top 10" },
  { icon: Sparkles, label: "Visible increase in engagement" },
  { icon: MapPin, label: "Growth in local footfall" },
  { icon: ArrowUpRight, label: "Boost client retention with continuous growth" },
  { icon: Coins, label: "Unlock more revenue from your existing clients" },
  { icon: ArrowLeftRight, label: "Easily upsell to your entire client base" },
  { icon: Settings, label: "Run your entire setup with zero additional employees" },
  { icon: Zap, label: "Already managing social media? Supercharge it with GMB." },
];

const TRUST_POINTS = [
  { icon: Shield, title: "Authorized Google API partner", sub: "Secure integration" },
  { icon: Users, title: "Used by 1000+ locations", sub: "Trusted by businesses worldwide" },
  { icon: Headphones, title: "24/7 support", sub: "Always here when you need us" },
];

const WHY_AGENCIES = [
  { icon: TrendingUp, title: "3x higher rankings", text: "Proven methodology that consistently delivers triple the ranking improvements for our agency partners." },
  { icon: Users, title: "45% more leads", text: "Our optimized GMB strategies generate nearly 45% more qualified leads than traditional approaches." },
  { icon: MessageSquare, title: "AI review management", text: "Intelligent automation that responds to reviews professionally and maintains your reputation 24/7." },
  { icon: Users2, title: "Citation networks", text: "Comprehensive citation building across 10-15 authoritative directories and local listing sites." },
  { icon: BarChart, title: "Client management dashboard", text: "Manage all your clients' profiles from one centralized dashboard with detailed reporting." },
  { icon: BarChart3, title: "ROI-focused analytics", text: "Clear, actionable insights that demonstrate real business impact and return on investment." },
];

const GMB_PROBLEMS = [
  { icon: AlertTriangle, title: "GMB suspension removal", proof: "94% success rate", text: "Get suspended accounts reinstated quickly with our proven appeal process." },
  { icon: Phone, title: "Phone number not updating", proof: "Technical experts", text: "Our specialists fix stubborn phone number issues that Google won't resolve." },
  { icon: MapPin, title: "Get your location verified", proof: "Challenging cases", text: "We handle the most difficult verification scenarios other services can't solve." },
  { icon: Users, title: "Perfect GMB profile setup", proof: "No cost", text: "Complete optimization of your GMB profiles at no additional charge." },
];

const STEPS = [
  { title: "Audit and identify", text: "We perform a comprehensive audit of your current GMB setup and identify all issues affecting your client relationships." },
  { title: "Optimize and automate", text: "Our platform automatically fixes common issues and sets up robust systems to prevent future problems." },
  { title: "Scale and succeed", text: "Watch your client satisfaction soar as you deliver consistent, reliable GMB management with detailed reporting." },
];

const CORE_FEATURES = [
  { icon: Users2, title: "Profile management", text: "Complete control over your Google My Business profiles with automated updates and optimization.", to: "/features/profile-management", cta: "Explore profile management" },
  { icon: MapPin, title: "Multi-location management", text: "Manage hundreds of locations from one dashboard with bulk editing and synchronized updates.", to: "/features/multi-location-management", cta: "Explore multi-location management" },
  { icon: BarChart3, title: "Customer insights", text: "Deep analytics and customer behavior insights to help you make data-driven decisions.", to: "/features/customer-insights", cta: "Explore customer insights" },
  { icon: MessageSquare, title: "Reputation management", text: "Automated review responses and reputation monitoring to maintain your online presence.", to: "/features/reputation-management", cta: "Explore reputation management" },
  { icon: FileText, title: "Content management", text: "Streamlined content creation and posting across all your business locations.", to: "/features/content-management", cta: "Explore content management" },
];

const Agency = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [sendState, setSendState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [api, setApi] = useState<any>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // The audit request reaches Anand and Manu by email (Anand, 17 Oct 26: "wire them up and let them land in email
  // to me and manu"); it used to stop at console.log.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sendState === 'sending') return;
    setSendState('sending');
    try {
      await sendSiteLead('agency-page', { name: formData.name, email: formData.email, phone: formData.phone, businessName: formData.company });
      setSendState('sent');
    } catch (err) {
      console.error('[Agency] audit request did not send:', err);
      setSendState('failed');
    }
  };

  // Auto-scroll effect for carousel
  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, 2000);
    return () => clearInterval(interval);
  }, [api]);


  return (
    <div className="min-h-screen font-['Inter']">
      <Helmet>
        <title>GMB Management Tool for Marketing Agencies | Fly</title>
        <meta name="description" content="Manage your clients' Google Business Profiles from the Fly web dashboard: profile optimization, content publishing, rank tracking, and review management." />
        <meta name="keywords" content="GMB management tool for agencies, Google Business Profile management, local SEO platform, agency GMB tool, multi-client management, GBP dashboard, review management tool" />
        <link rel="canonical" href="https://www.fly-social.com/agency" />

        {/* Open Graph tags */}
        <meta property="og:title" content="GMB Management Tool for Marketing Agencies | Fly Web Dashboard" />
        <meta property="og:description" content="Manage your clients' Google Business Profiles with Fly's Web Dashboard. Profile optimization, content publishing, rank tracking, review management & more." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.fly-social.com/agency" />
        <meta property="og:image" content="https://www.fly-social.com/lovable-uploads/5e360c99-b89f-455c-80a1-66fed5df1555.png" />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="GMB Management Tool for Marketing Agencies | Fly Web Dashboard" />
        <meta name="twitter:description" content="Manage your clients' Google Business Profiles with Fly's Web Dashboard. Profile optimization, content publishing, rank tracking, review management & more." />
        <meta name="twitter:image" content="https://www.fly-social.com/lovable-uploads/5e360c99-b89f-455c-80a1-66fed5df1555.png" />

        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Fly Web Dashboard for Agencies",
            "description": "Complete Local SEO management platform for marketing agencies to manage their clients' Google Business Profiles",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "provider": {
              "@type": "Organization",
              "name": "Fly - ZoomLocal Tech",
              "url": "https://www.fly-social.com"
            },
            "offers": {
              "@type": "Offer",
              "price": "9.99",
              "priceCurrency": "USD",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "9.99",
                "priceCurrency": "USD",
                "unitText": "per location per month"
              }
            },
            "featureList": [
              "Profile Optimization",
              "Content Publishing",
              "Rank Tracking",
              "Review Management",
              "Performance Analytics",
              "Multi-location Management"
            ]
          })}
        </script>
      </Helmet>
      <NavBar />

      {/* Hero Section */}
      <section
        className="pt-24 md:pt-32 pb-16 lg:pb-20 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f0fdf4 40%, #ecfeff 100%)' }}
      >
        <FloatingOrbs variant="hero" />

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-start gap-12 max-w-[1280px] mx-auto">
            {/* Left Column - Content */}
            <div className="lg:w-1/2">
              <RevealOnScroll>
                <h1
                  className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-6"
                  style={{ color: LANDING_COLORS.text.primary }}
                >
                  Manage Client Profiles{' '}
                  <span style={{ color: LANDING_COLORS.primary }}>With Full Control</span>{' '}
                  One Dashboard
                </h1>
              </RevealOnScroll>

              <RevealOnScroll delay={1}>
                {/* Outcomes, one style for every box */}
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  {HERO_OUTCOMES.map(({ icon: Icon, label }) => (
                    <li key={label} className="bg-white border border-purple-100 rounded-2xl p-4 flex items-center gap-3">
                      <Icon className="h-5 w-5 flex-shrink-0" style={{ color: LANDING_COLORS.primary }} aria-hidden />
                      <span className="text-base font-medium text-gray-800 leading-snug">{label}</span>
                    </li>
                  ))}
                </ul>
              </RevealOnScroll>
            </div>

            {/* Right Column - Visual Card */}
            <div className="lg:w-1/2">
              <RevealOnScroll delay={2} direction="right">
                <div className="text-center">
                  <div className="bg-white rounded-3xl p-6 lg:p-7 shadow-xl border border-purple-100 mb-8">
                    <img
                      src="/lovable-uploads/5e360c99-b89f-455c-80a1-66fed5df1555.png"
                      alt="Add GMB Management service - Boost Retention and Generate 10x Revenue"
                      className="w-full h-auto rounded-2xl"
                    />
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
                    <a href="https://www.fly-social.com/fly-app" target="_blank" rel="noopener noreferrer">
                      <Button className="bg-violet-600 hover:bg-violet-700 text-white font-bold px-8 py-3 text-base rounded-2xl shadow-lg transition-colors">
                        Start free for 14 days
                      </Button>
                    </a>
                    <Link to="/pricing">
                      <Button
                        variant="outline"
                        className="border-2 border-violet-600 text-violet-700 bg-white font-semibold px-8 py-3 text-base rounded-2xl hover:bg-violet-600 hover:text-white transition-colors"
                      >
                        See pricing
                      </Button>
                    </Link>
                  </div>

                  <p className="text-base font-semibold" style={{ color: LANDING_COLORS.primary }}>
                    14 days free, no credit card required
                  </p>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 bg-violet-700 text-white">
        <div className="container mx-auto px-4">
          <ul className="grid md:grid-cols-3 gap-8 text-center">
            {TRUST_POINTS.map(({ icon: Icon, title, sub }) => (
              <li key={title}>
                <Icon className="h-8 w-8 mx-auto mb-3" aria-hidden />
                <p className="text-lg font-semibold mb-1">{title}</p>
                <p className="text-sm text-white/90">{sub}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Trusted by Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-foreground mb-10 text-center">Trusted by top consumer brands across the globe</h2>

          <div className="max-w-5xl mx-auto">
            <Carousel
              opts={{
                align: "start",
                loop: true,
                dragFree: true
              }}
              setApi={setApi}
              className="relative px-10"
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {brandLogos.map((brand, index) => (
                  <CarouselItem key={index} className="pl-2 md:pl-4 basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/4 xl:basis-1/5 flex items-center justify-center">
                    <div className="h-36 sm:h-40 md:h-44 w-full flex items-center justify-center p-4 rounded-xl bg-white">
                      <img
                        src={brand.logo}
                        alt={`${brand.name} logo`}
                        className="w-full h-full object-contain"
                        style={{ maxWidth: '200px', maxHeight: '140px' }}
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-0 bg-white hover:bg-white shadow-md border-0" />
              <CarouselNext className="right-0 bg-white hover:bg-white shadow-md border-0" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Why Agencies Choose Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            Why top agencies choose <span className="text-violet-700">Fly's Web Dashboard</span>
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Full control over your clients' Local SEO, with every feature in one dashboard.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16 max-w-6xl mx-auto">
            {WHY_AGENCIES.map(({ icon: Icon, title, text }) => (
              <div key={title} className="p-6 bg-white border border-gray-200 rounded-2xl">
                <div className="flex items-center gap-3 mb-3">
                  <Icon className="h-6 w-6 flex-shrink-0 text-violet-700" aria-hidden />
                  <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GMB Problems Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl lg:text-5xl font-bold text-center mb-6 text-gray-900">
            GMB problems that <span className="text-violet-700">kill agency relationships?</span> We fix them all.
          </h2>
          <p className="text-center text-gray-600 mb-16 max-w-3xl mx-auto text-lg">
            Stop losing clients to GMB issues. Our specialist team solves the problems that destroy agency relationships.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {GMB_PROBLEMS.map(({ icon: Icon, title, proof, text }) => (
              <div key={title} className="p-6 bg-white rounded-xl border border-gray-200 flex flex-col h-full">
                <Icon className="h-7 w-7 mb-4 text-violet-700" aria-hidden />
                <h3 className="text-xl font-bold mb-1 text-gray-900">{title}</h3>
                <p className="text-violet-700 font-bold text-lg mb-3">{proof}</p>
                <p className="text-gray-600 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 Steps Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            From crisis to <span className="text-violet-700">success</span> in 3 steps
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Our proven method turns GMB problems into an advantage for your agency.
          </p>

          <ol className="flex flex-col md:flex-row items-center md:items-start justify-center gap-10 md:gap-6 mb-16">
            {STEPS.map(({ title, text }, i) => (
              <li key={title} className="flex flex-col md:flex-row items-center md:items-start gap-6">
                <div className="text-center max-w-xs">
                  <div className="bg-violet-700 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                    {i + 1}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900">{title}</h3>
                  <p className="text-gray-600 text-sm">{text}</p>
                </div>
                {i < STEPS.length - 1 && (
                  <ArrowRight className="hidden md:block h-8 w-8 mt-2 text-gray-300 flex-shrink-0" aria-hidden />
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Five Core Features */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-6 text-gray-900">
            Five core features
          </h2>
          <p className="text-center text-gray-600 mb-16 max-w-3xl mx-auto text-lg">
            Everything you need to manage your Google My Business presence well.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {CORE_FEATURES.map(({ icon: Icon, title, text, to, cta }) => (
              <div key={title} className="p-6 bg-white rounded-xl border border-gray-200 flex flex-col h-full">
                <Icon className="h-7 w-7 mb-4 text-violet-700" aria-hidden />
                <h3 className="text-xl font-bold mb-3 text-gray-900">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">{text}</p>
                <Link to={to} className="mt-auto">
                  <Button className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-3 rounded-lg w-full whitespace-normal h-auto">
                    {cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Generation Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">Start managing client profiles today</h2>
          <p className="text-center text-gray-600 mb-12">Get a free GMB audit for any client and see how Fly can transform their Local SEO.</p>

          <div className="flex flex-col lg:flex-row items-start gap-12 max-w-6xl mx-auto">
            <div className="lg:w-1/2 w-full">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                {sendState === 'sent' ? (
                  <div role="status" className="space-y-2 py-6 text-center">
                    <CheckCircle className="mx-auto h-8 w-8 text-violet-700" aria-hidden />
                    <p className="text-lg font-bold text-gray-900">Thanks, we have your details.</p>
                    <p className="text-gray-600">We'll be in touch within 24 hours with your free GMB audit. A copy of your request is on its way to {formData.email}.</p>
                  </div>
                ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="text"
                    name="name"
                    aria-label="Your name"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="h-12 text-base border-gray-300"
                  />
                  <Input
                    type="email"
                    name="email"
                    aria-label="Email address"
                    placeholder="Email address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="h-12 text-base border-gray-300"
                  />
                  <Input
                    type="tel"
                    name="phone"
                    aria-label="Phone number"
                    placeholder="Phone number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="h-12 text-base border-gray-300"
                  />
                  <Input
                    type="text"
                    name="company"
                    aria-label="Agency or company name"
                    placeholder="Agency or company name"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="h-12 text-base border-gray-300"
                  />
                  <Button type="submit" disabled={sendState === 'sending'} className="w-full bg-violet-600 hover:bg-violet-700 text-white py-3 h-12 text-base font-bold rounded-lg">
                    {sendState === 'sending' ? 'Sending...' : 'Get my free GMB audit'}
                  </Button>
                  {sendState === 'failed' && (
                    <p role="alert" className="text-sm font-medium text-red-700">{SITE_LEAD_ERROR}</p>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Shield className="h-4 w-4 text-violet-700 flex-shrink-0" aria-hidden />
                    <span>Your information is secure and will only be used to send your GMB audit.</span>
                  </div>
                </form>
                )}
              </div>
            </div>

            <div className="lg:w-1/2 w-full">
              {/* Illustration of the profile the audit reads */}
              <div className="bg-blue-50 rounded-lg p-6 mb-6" aria-hidden>
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-600 text-white rounded-lg p-2 w-10 h-10 flex items-center justify-center text-lg font-bold">
                    G
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Your Business Name</div>
                    <div className="text-sm text-gray-600">Business Category</div>
                    <div className="text-sm text-gray-600 flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      Your Location
                    </div>
                  </div>
                </div>
                <div className="flex text-yellow-400">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="h-4 w-4 fill-current" />
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-bold mb-2 text-gray-900">GMB emergency? Call us:</h3>
                <div className="flex items-center gap-2 text-2xl font-bold mb-4 text-blue-700">
                  <Phone className="h-6 w-6" aria-hidden />
                  <a href="tel:+918446712833" className="hover:underline">+91 8446712833</a>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-700" aria-hidden />
                    <span className="text-gray-700">Immediate GMB problem diagnosis</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-700" aria-hidden />
                    <span className="text-gray-700">Free audit reveals hidden ranking issues</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Agency;